import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  message,
  Card,
  Radio,
  Statistic,
  Row,
  Col,
  Tag,
  Modal,
  Form,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  GroupOutlined,
  DeleteOutlined,
  LineChartOutlined,
  FileTextOutlined,
  EditOutlined,
} from "@ant-design/icons";
import moment from "moment";
import apiService from "../../services/api";
import ErrorBoundary from "../common/ErrorBoundary";
import "./TradeDataTable.css";
import {
  formatVolume,
  formatExchangeName,
  formatTradeType,
  getTradeTypeColor,
  formatDate,
  formatDateTime,
} from "../../utils/formatters";
import { useAuth } from "../../hooks/useAuth";
import { useAdminPanoramaUsers } from "../../hooks/useAdminPanoramaUsers";
import { withOwnerUserId } from "../../utils/ownerScopeParams";

const { RangePicker } = DatePicker;
const { Option } = Select;

/** 账号筛选：账号、交易所、交易类型组合（与活跃账号 API 一致） */
const ACCOUNT_SCOPE_SEP = "\t";

const TradeDataTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0); // Added total state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [viewMode, setViewMode] = useState("list"); // 'list' | 'grouped' | 'byAccount'
  const [groupedData, setGroupedData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { adminUsers, loadingUsers: loadingAdminUsers } = useAdminPanoramaUsers();

  // 时间快捷选项
  const timePresets = [
    { label: "1天", value: "1d" },
    { label: "3天", value: "3d" },
    { label: "7天", value: "7d" },
    { label: "近30天", value: "30d" },
    { label: "本月", value: "thisMonth" },
    { label: "上月", value: "lastMonth" },
    { label: "自定义日期", value: "custom" },
  ];

  const [timePreset, setTimePreset] = useState("");
  const [showCustomRange, setShowCustomRange] = useState(false);

  // 计算时间范围
  const getDateRangeFromPreset = (preset) => {
    const today = moment();
    const yesterday = today.clone().subtract(1, "days");
    switch (preset) {
      case "1d":
        return [yesterday.clone().startOf("day"), yesterday.clone().endOf("day")];
      case "3d":
        return [today.clone().subtract(3, "days").startOf("day"), yesterday.clone().endOf("day")];
      case "7d":
        return [today.clone().subtract(7, "days").startOf("day"), yesterday.clone().endOf("day")];
      case "30d":
        return [today.clone().subtract(30, "days").startOf("day"), yesterday.clone().endOf("day")];
      case "thisMonth":
        return [today.clone().startOf("month"), today.clone().endOf("month")];
      case "lastMonth":
        return [
          today.clone().subtract(1, "month").startOf("month"),
          today.clone().subtract(1, "month").endOf("month"),
        ];
      default:
        return null;
    }
  };

  // 筛选状态
  const [filters, setFilters] = useState({
    exchange: "",
    pair: "",
    trade_type: "",
    dateRange: null,
    source: "",
    account: "",
    account_like: "",
    owner_user_id: undefined,
    token_id: "",
    hash: "",
  });

  // 活跃账号状态
  const [activeAccounts, setActiveAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // 交易所列表状态
  const [exchanges, setExchanges] = useState([]);
  const [loadingExchanges, setLoadingExchanges] = useState(false);

  // 排序状态
  const [sorter, setSorter] = useState({
    field: "created_at",
    order: "descend",
  });

  // 搜索状态
  const [searchText, setSearchText] = useState("");

  // 统计数据
  const [stats, setStats] = useState({
    totalVolume: 0,
    makerVolume: 0,
    takerVolume: 0,
    totalFee: 0,
    recordCount: 0,
  });

  // 获取数据
  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const currentSortOrder = params.sortOrder || sorter.order;
      const { dateRange, owner_user_id, token_id, hash, ...restFilters } = filters;

      const queryParams = {
        page: params.page || pagination.current,
        page_size: params.pageSize || pagination.pageSize,
        order_by: params.sortField || sorter.field,
        order_dir: currentSortOrder === "ascend" ? "asc" : "desc",
      };

      Object.keys(restFilters).forEach((key) => {
        const v = restFilters[key];
        if (v !== "" && v !== null && v !== undefined) {
          queryParams[key] = v;
        }
      });

      if (dateRange && dateRange.length === 2) {
        queryParams.date_from = dateRange[0].format("YYYY-MM-DD");
        queryParams.date_to = dateRange[1].format("YYYY-MM-DD");
      }

      const q = searchText.trim();
      if (q) {
        queryParams.search = q;
      }

      if (isAdmin && owner_user_id !== undefined && owner_user_id !== null && owner_user_id !== "") {
        queryParams.owner_user_id = owner_user_id;
      }

      if (isAdmin && token_id !== "" && token_id != null) {
        const tid = parseInt(String(token_id).trim(), 10);
        if (!Number.isNaN(tid) && tid > 0) {
          queryParams.token_id = tid;
        }
      }

      if (isAdmin && hash && String(hash).trim()) {
        queryParams.hash = String(hash).trim();
      }

      const response = await apiService.trades.getTrades(queryParams);

      if (!response.data.success) {
        throw new Error(response.data.message || "获取交易数据失败");
      }

      const result = response.data.data || {};
      const tradeList = result.list || [];

      setData(tradeList);
      setSelectedRowKeys([]); // Reset selection on data fetch
      setPagination((prev) => ({
        ...prev,
        current: result.page || 1,
        pageSize: result.page_size || 20,
        total: result.total || 0,
      }));
      setTotal(result.total || 0); // Update total state

      // 计算统计数据
      calculateStats(tradeList);

      if (viewMode === "grouped") {
        processGroupedDataByExchange(tradeList);
      } else if (viewMode === "byAccount") {
        processGroupedDataByAccount(tradeList);
      }
    } catch (error) {
      message.error(
        "加载数据失败: " +
        (error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message),
      );
    } finally {
      setLoading(false);
    }
  };

  // 计算统计数据
  const calculateStats = (tradeList) => {
    const totalVolume = tradeList.reduce(
      (sum, item) => sum + parseFloat(item.volume || 0),
      0,
    );
    const makerVolume = tradeList.reduce(
      (sum, item) => sum + parseFloat(item.maker_volume || 0),
      0,
    );
    const takerVolume = tradeList.reduce(
      (sum, item) => sum + parseFloat(item.taker_volume || 0),
      0,
    );
    const totalFee = tradeList.reduce(
      (sum, item) => sum + parseFloat(item.total_fee || 0),
      0,
    );

    setStats({
      totalVolume,
      makerVolume,
      takerVolume,
      totalFee,
      recordCount: tradeList.length,
    });
  };

  const processGroupedDataByExchange = (tradeList) => {
    const grouped = {};

    tradeList.forEach((item) => {
      const exchange = item.exchange || "Unknown";
      if (!grouped[exchange]) {
        grouped[exchange] = {
          exchange,
          items: [],
          totalVolume: 0,
          count: 0,
        };
      }

      grouped[exchange].items.push(item);
      grouped[exchange].totalVolume += parseFloat(item.volume || 0);
      grouped[exchange].count += 1;
    });

    setGroupedData(Object.values(grouped));
  };

  /** 按账号+交易所汇总，现货与合约分列 */
  const processGroupedDataByAccount = (tradeList) => {
    const grouped = {};
    tradeList.forEach((item) => {
      const acc = item.account || "-";
      const ex = item.exchange || "Unknown";
      const key = `${acc}${ACCOUNT_SCOPE_SEP}${ex}`;
      if (!grouped[key]) {
        grouped[key] = {
          key,
          account: acc,
          exchange: ex,
          spotVolume: 0,
          swapVolume: 0,
          totalVolume: 0,
          count: 0,
        };
      }
      const g = grouped[key];
      const vol = parseFloat(item.volume || 0);
      g.totalVolume += vol;
      g.count += 1;
      if (item.trade_type === "spot") {
        g.spotVolume += vol;
      } else if (item.trade_type === "swap") {
        g.swapVolume += vol;
      }
    });
    setGroupedData(Object.values(grouped));
  };

  // 初始加载
  useEffect(() => {
    fetchData();
  }, [viewMode]);

  // 获取活跃账号列表
  const fetchActiveAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const base = {
        limit: 100,
        date_from: filters.dateRange?.[0]?.format("YYYY-MM-DD"),
        date_to: filters.dateRange?.[1]?.format("YYYY-MM-DD"),
      };
      const params = isAdmin
        ? withOwnerUserId(base, filters.owner_user_id ?? null)
        : base;
      const response = await apiService.trades.getActiveAccounts(params);

      if (response.data.success) {
        setActiveAccounts(response.data.data || []);
      }
    } catch (error) {
      console.error("获取活跃账号失败:", error);
    } finally {
      setLoadingAccounts(false);
    }
  }, [filters.dateRange, filters.owner_user_id, isAdmin]);

  // 初始加载活跃账号（日期或管理员所选用户变化时刷新）
  useEffect(() => {
    fetchActiveAccounts();
  }, [fetchActiveAccounts]);

  // 获取交易所列表
  const fetchExchanges = async () => {
    setLoadingExchanges(true);
    try {
      // 优先从交易所管理列表获取；如果失败则从交易数据中获取唯一值
      const response = await apiService.exchanges.getActiveExchanges();
      if (response.data.success) {
        const exchangeList = response.data.data || [];
        setExchanges(exchangeList);
      }
    } catch (error) {
      console.error("获取交易所列表失败:", error);
      // 降级：从交易所统计接口获取
      try {
        const statsResponse = await apiService.trades.getExchangeStats({ limit: 100 });
        if (statsResponse.data.success) {
          const statsData = statsResponse.data.data || [];
          // 去重
          const uniqueExchanges = [...new Set(statsData.map((s) => s.exchange))].map((ex) => ({ name: ex, code: ex }));
          setExchanges(uniqueExchanges);
        }
      } catch (fallbackError) {
        console.error("降级获取交易所失败:", fallbackError);
      }
    } finally {
      setLoadingExchanges(false);
    }
  };

  // 初始加载交易所列表
  useEffect(() => {
    fetchExchanges();
  }, []);


  const handleDelete = async (id) => {
    try {
      const response = await apiService.trades.deleteTrade(id);
      if (response.data.success) {
        message.success("删除成功");
        fetchData();
      }
    } catch (error) {
      message.error(
        "删除失败: " +
        (error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message),
      );
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      const response = await apiService.trades.deleteTradesBatch(selectedRowKeys); // Changed to apiService.trades.deleteTradesBatch
      if (response.data?.success) {
        message.success(`批量删除 ${selectedRowKeys.length} 条数据成功`); // Changed message
        setSelectedRowKeys([]);
        fetchData();
        // fetchStats(); // Assuming fetchStats is a function that updates stats, if not, remove or define it.
      }
    } catch (error) {
      message.error(
        "批量删除失败: " +
        (error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message),
      );
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setIsEditModalOpen(true);
    setTimeout(() => {
      form.setFieldsValue({
        ...record,
        timestamp: record.timestamp ? moment(record.timestamp) : null,
        created_at: record.created_at ? moment(record.created_at) : null,
        updated_at: record.updated_at ? moment(record.updated_at) : null,
      });
    }, 0);
  };

  const handleEditSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        timestamp: values.timestamp ? values.timestamp.toISOString() : null,
        created_at: values.created_at ? values.created_at.toISOString() : null,
        updated_at: values.updated_at ? values.updated_at.toISOString() : null,
      };
      const response = await apiService.trades.updateTrade(editingRecord.id, payload); // Changed to apiService.trades.updateTrade
      if (response.data?.success) {
        message.success("更新成功");
        setIsEditModalOpen(false);
        fetchData();
        // fetchStats(); // Assuming fetchStats is a function that updates stats, if not, remove or define it.
      }
    } catch (error) {
      message.error("更新失败: " + (error.response?.data?.error?.message || error.response?.data?.message || error.message)); // Added error handling
    }
  };

  // 打开新增弹窗
  const handleOpenAdd = () => {
    addForm.resetFields();
    setIsAddModalOpen(true);
  };

  // 提交新增
  const handleAddSubmit = async () => {
    try {
      const values = await addForm.validateFields();
      if (!values.timestamp) {
        message.error("请选择交易时间");
        return;
      }
      const payload = {
        ...values,
        timestamp: values.timestamp.toISOString(),
        created_at: values.created_at ? values.created_at.toISOString() : null,
        updated_at: values.updated_at ? values.updated_at.toISOString() : null,
      };
      const response = await apiService.trades.createTradeManual(payload);
      if (response.data?.success) {
        message.success("新增记录成功");
        setIsAddModalOpen(false);
        fetchData();
      }
    } catch (error) {
      if (error.errorFields) return; // Validation error
      message.error("新增失败: " + (error.response?.data?.error?.message || error.response?.data?.message || error.message));
    }
  };

  // 列表模式的列定义
  const listColumns = [
    {
      title: "交易所",
      dataIndex: "exchange",
      key: "exchange",
      width: 120,
      sorter: true,
      fixed: "left",
      render: (text) => (
        <span style={{ fontWeight: 500, color: "#1890ff" }}>
          {formatExchangeName(text)}
        </span>
      ),
    },
    {
      title: "类型",
      dataIndex: "trade_type",
      key: "trade_type",
      width: 80,
      render: (tradeType) => (
        <Tag color={getTradeTypeColor(tradeType)}>
          {formatTradeType(tradeType)}
        </Tag>
      ),
    },
    {
      title: "账号",
      dataIndex: "account",
      key: "account",
      width: 120,
      render: (text) => (
        <span style={{ fontWeight: 500, color: "#fa8c16" }}>{text || "-"}</span>
      ),
    },
    {
      title: "交易对",
      dataIndex: "pair",
      key: "pair",
      width: 120,
      sorter: true,
      render: (text) => (
        <span style={{ fontWeight: 500, color: "#722ed1" }}>{text}</span>
      ),
    },
    {
      title: "交易对价",
      dataIndex: "price",
      key: "price",
      width: 100,
      align: "right",
      render: (v) => (
        <span style={{ fontFamily: "monospace", color: "#666" }}>
          {v != null && Number(v) !== 0 ? formatVolume(v) : "—"}
        </span>
      ),
    },
    {
      title: "手续费币价",
      dataIndex: "fee_token_price",
      key: "fee_token_price",
      width: 110,
      align: "right",
      render: (v) => (
        <Tooltip title="折算完成后为 1（表示手续费金额已是 USDT）；入库前非 U 本位币用 fee_token_price 折算">
          <span style={{ fontFamily: "monospace", color: "#666" }}>
            {v != null && Number(v) !== 0 ? formatVolume(v) : "—"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "总交易量",
      dataIndex: "volume",
      key: "volume",
      width: 140,
      sorter: true,
      render: (volume) => (
        <span
          style={{ fontFamily: "monospace", fontWeight: 500, color: "#13c2c2" }}
        >
          {formatVolume(volume)}
        </span>
      ),
      align: "right",
    },
    {
      title: "做市量",
      dataIndex: "maker_volume",
      key: "maker_volume",
      width: 120,
      sorter: true,
      render: (volume) => (
        <span style={{ fontFamily: "monospace", color: "#52c41a" }}>
          {formatVolume(volume)}
        </span>
      ),
      align: "right",
    },
    {
      title: "吃单量",
      dataIndex: "taker_volume",
      key: "taker_volume",
      width: 120,
      sorter: true,
      render: (volume) => (
        <span style={{ fontFamily: "monospace", color: "#faad14" }}>
          {formatVolume(volume)}
        </span>
      ),
      align: "right",
    },
    {
      title: "做市手续费(USDT)",
      dataIndex: "maker_fee",
      key: "maker_fee",
      width: 120,
      render: (fee) => (
        <span style={{ fontFamily: "monospace", color: "#52c41a" }}>
          {formatVolume(fee)}
        </span>
      ),
      align: "right",
    },
    {
      title: "吃单手续费(USDT)",
      dataIndex: "taker_fee",
      key: "taker_fee",
      width: 120,
      render: (fee) => (
        <span style={{ fontFamily: "monospace", color: "#faad14" }}>
          {formatVolume(fee)}
        </span>
      ),
      align: "right",
    },
    {
      title: "总手续费(USDT)",
      dataIndex: "total_fee",
      key: "total_fee",
      width: 120,
      render: (fee) => (
        <span style={{ fontFamily: "monospace", fontWeight: 500, color: "#eb2f96" }}>
          {formatVolume(fee)}
        </span>
      ),
      align: "right",
    },
    {
      title: "手续费计价",
      dataIndex: "fee_currency",
      key: "fee_currency",
      width: 100,
      render: (currency) => (
        <Tooltip title="入库统一为 USDT 等值；多币种原数据会合并为一条">
          <Tag color="green">{currency || "USDT"}</Tag>
        </Tooltip>
      ),
      align: "center",
    },
    {
      title: "交易日期",
      dataIndex: "timestamp",
      key: "trade_date",
      width: 120,
      sorter: true,
      render: (ts) => ts ? moment(ts).format("YYYY-MM-DD") : "-",
    },
    {
      title: "数据源",
      dataIndex: "source",
      key: "source",
      width: 80,
      render: (text) => {
        const color =
          text === "API" ? "#108ee9" : text === "Manual" ? "#f50" : "#87d068";
        return (
          <span style={{ color, fontWeight: 500, fontSize: "12px" }}>
            {text}
          </span>
        );
      },
      align: "center",
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      sorter: true,
      render: (created_at) => formatDateTime(created_at),
    },
    ...(isAdmin ? [{
      title: "操作",
      key: "action",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定删除此记录？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  // 分组模式的列定义
  const groupedColumns = [
    {
      title: "交易所",
      dataIndex: "exchange",
      key: "exchange",
      width: 150,
      fixed: "left",
      render: (text) => (
        <span style={{ fontWeight: 600, fontSize: "16px", color: "#1890ff" }}>
          {formatExchangeName(text)}
        </span>
      ),
    },
    {
      title: "记录数",
      dataIndex: "count",
      key: "count",
      width: 100,
      align: "right",
      render: (count) => <span style={{ fontWeight: 500 }}>{count}</span>,
    },
    {
      title: "总交易量",
      dataIndex: "totalVolume",
      key: "totalVolume",
      width: 180,
      align: "right",
      render: (volume) => (
        <span
          style={{
            fontFamily: "monospace",
            fontWeight: 600,
            fontSize: "14px",
            color: "#13c2c2",
          }}
        >
          {formatVolume(volume)}
        </span>
      ),
    },
  ];

  const groupedColumnsByAccount = [
    {
      title: "账号",
      dataIndex: "account",
      key: "account",
      width: 140,
      fixed: "left",
      render: (text) => (
        <span style={{ fontWeight: 600, color: "#fa8c16" }}>{text}</span>
      ),
    },
    {
      title: "交易所",
      dataIndex: "exchange",
      key: "exchange",
      width: 130,
      render: (text) => (
        <span style={{ fontWeight: 500, color: "#1890ff" }}>{formatExchangeName(text)}</span>
      ),
    },
    {
      title: "现货交易量",
      dataIndex: "spotVolume",
      key: "spotVolume",
      width: 150,
      align: "right",
      render: (v) => (
        <span style={{ fontFamily: "monospace", color: "#52c41a" }}>{formatVolume(v)}</span>
      ),
    },
    {
      title: "合约交易量",
      dataIndex: "swapVolume",
      key: "swapVolume",
      width: 150,
      align: "right",
      render: (v) => (
        <span style={{ fontFamily: "monospace", color: "#722ed1" }}>{formatVolume(v)}</span>
      ),
    },
    {
      title: "合计",
      dataIndex: "totalVolume",
      key: "totalVolume",
      width: 150,
      align: "right",
      render: (volume) => (
        <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#13c2c2" }}>
          {formatVolume(volume)}
        </span>
      ),
    },
    {
      title: "记录数",
      dataIndex: "count",
      key: "count",
      width: 100,
      align: "right",
      render: (count) => <span style={{ fontWeight: 500 }}>{count}</span>,
    },
  ];

  const accountGroupSummary = useMemo(() => {
    if (viewMode !== "byAccount" || !groupedData.length) {
      return null;
    }
    return groupedData.reduce(
      (acc, g) => ({
        spotVolume: acc.spotVolume + (g.spotVolume || 0),
        swapVolume: acc.swapVolume + (g.swapVolume || 0),
        totalVolume: acc.totalVolume + (g.totalVolume || 0),
        count: acc.count + (g.count || 0),
      }),
      { spotVolume: 0, swapVolume: 0, totalVolume: 0, count: 0 },
    );
  }, [viewMode, groupedData]);

  const accountSelectValue = useMemo(() => {
    if (!filters.account) {
      return undefined;
    }
    const match = activeAccounts.find(
      (a) =>
        a.account === filters.account &&
        a.exchange === filters.exchange &&
        a.trade_type === filters.trade_type,
    );
    return match
      ? `${match.account}${ACCOUNT_SCOPE_SEP}${match.exchange}${ACCOUNT_SCOPE_SEP}${match.trade_type}`
      : undefined;
  }, [filters.account, filters.exchange, filters.trade_type, activeAccounts]);

  const handleAccountFilterChange = (value) => {
    if (!value) {
      setFilters((prev) => ({
        ...prev,
        account: "",
        exchange: "",
        trade_type: "",
      }));
      return;
    }
    const parts = value.split(ACCOUNT_SCOPE_SEP);
    const [account, exchange, tradeType] = parts;
    setFilters((prev) => ({
      ...prev,
      account,
      exchange,
      trade_type: tradeType,
      account_like: "",
    }));
  };

  // 处理表格变化
  const handleTableChange = (paginationConfig, filtersConfig, sorterConfig) => {
    if (sorterConfig && sorterConfig.field) {
      const newSorter = {
        field: sorterConfig.field || "created_at",
        order: sorterConfig.order || "descend",
      };
      setSorter(newSorter);

      fetchData({
        page: paginationConfig?.current || pagination.current,
        pageSize: paginationConfig?.pageSize || pagination.pageSize,
        sortField: newSorter.field,
        sortOrder: newSorter.order,
      });
    } else {
      fetchData({
        page: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
        sortField: sorter.field,
        sortOrder: sorter.order,
      });
    }
  };

  // 处理筛选变化
  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchData({ page: 1 });
  };

  // 重置筛选
  const handleReset = () => {
    setFilters({
      exchange: "",
      pair: "",
      trade_type: "",
      dateRange: null,
      source: "",
      account: "",
      account_like: "",
      owner_user_id: undefined,
      token_id: "",
      hash: "",
    });
    setTimePreset("");
    setShowCustomRange(false);
    setSearchText("");
    setSorter({
      field: "created_at",
      order: "descend",
    });
    setPagination({ ...pagination, current: 1 });
    fetchData({ page: 1 });
  };

  // 刷新数据
  const handleRefresh = () => {
    fetchData();
  };

  // 导出数据
  const handleExport = async () => {
    try {
      message.loading("正在生成报告...", 0);

      const start =
        filters.dateRange?.[0]?.format("YYYY-MM-DD") ||
        moment().subtract(7, "days").format("YYYY-MM-DD");
      const end =
        filters.dateRange?.[1]?.format("YYYY-MM-DD") ||
        moment().subtract(1, "days").format("YYYY-MM-DD");
      const params = {
        name: `交易量导出_${start}_${end}`,
        description: filters.exchange
          ? `筛选交易所: ${filters.exchange}`
          : "从交易数据页发起",
        type: "custom",
        start_date: start,
        end_date: end,
      };

      const response = await apiService.reports.generateReport(params);

      if (!response.data.success) {
        throw new Error(response.data.message || "生成报告失败");
      }

      const reportId =
        response.data.data?.report_id ?? response.data.data?.id;
      if (reportId) {
        // 下载报告
        const downloadResponse =
          await apiService.reports.downloadReport(reportId);

        // 创建下载链接
        const url = window.URL.createObjectURL(
          new Blob([downloadResponse.data]),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `trade_report_${moment().format("YYYYMMDD")}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();

        message.destroy();
        message.success("报告导出成功");
      }
    } catch (error) {
      message.destroy();
      message.error(
        "导出失败: " +
        (error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message),
      );
    }
  };

  return (
    <ErrorBoundary>
      <div className="trade-data-table">
        {/* 统计卡片 */}
        <Row gutter={20} className="stats-row">
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card stat-card-volume">
              <div className="stat-header">
                <div className="stat-icon volume">
                  <LineChartOutlined />
                </div>
                <div className="stat-label">总交易量</div>
              </div>
              <div className="stat-value" style={{ color: "var(--color-primary)" }}>
                {formatVolume(stats.totalVolume)} <span className="stat-unit">USDT</span>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card stat-card-maker">
              <div className="stat-header">
                <div className="stat-icon volume">
                  <LineChartOutlined />
                </div>
                <div className="stat-label">做市量 / 吃单量</div>
              </div>
              <div className="stat-value" style={{ color: "#52c41a" }}>
                {formatVolume(stats.makerVolume)} <span className="stat-unit" style={{ color: "#faad14" }}>/ {formatVolume(stats.takerVolume)}</span>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card stat-card-fee">
              <div className="stat-header">
                <div className="stat-icon fee">
                  <FileTextOutlined />
                </div>
                <div className="stat-label">总手续费</div>
              </div>
              <div className="stat-value" style={{ color: "#eb2f96" }}>
                {formatVolume(stats.totalFee)} <span className="stat-unit">USDT</span>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card stat-card-count">
              <div className="stat-header">
                <div className="stat-icon records">
                  <FileTextOutlined />
                </div>
                <div className="stat-label">记录数</div>
              </div>
              <div className="stat-value" style={{ color: "var(--color-secondary)" }}>
                {stats.recordCount} <span className="stat-unit">条</span>
              </div>
            </Card>
          </Col>
        </Row>

        <Card title="交易数据管理" className="table-card">
          {/* 筛选工具栏 */}
          <div className="filter-toolbar">
            <Space wrap size="middle">
              <Input
                placeholder="关键词：账号、交易对、交易所、来源、哈希…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
                style={{ width: 260 }}
                prefix={<SearchOutlined />}
                allowClear
              />

              {isAdmin && (
                <Select
                  placeholder="归属用户（全部）"
                  allowClear
                  loading={loadingAdminUsers}
                  style={{ width: 220 }}
                  value={filters.owner_user_id}
                  onChange={(v) =>
                    setFilters((prev) => ({ ...prev, owner_user_id: v }))
                  }
                  showSearch
                  optionFilterProp="children"
                >
                  {adminUsers
                    .filter((u) => u.role === "user")
                    .map((u) => (
                      <Option key={u.id} value={u.id}>
                        {u.username}（ID {u.id}）
                      </Option>
                    ))}
                </Select>
              )}

              <Input
                placeholder="账号关键词（模糊）"
                value={filters.account_like}
                onChange={(e) =>
                  handleFilterChange("account_like", e.target.value)
                }
                style={{ width: 150 }}
                allowClear
              />

              <Select
                placeholder="选择交易所"
                value={filters.exchange || undefined}
                onChange={(value) => handleFilterChange("exchange", value || "")}
                style={{ width: 150 }}
                allowClear
                loading={loadingExchanges}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {exchanges.length > 0
                  ? exchanges.map((ex) => (
                    <Option key={ex.code || ex.name} value={ex.code || ex.name}>
                      {ex.name || ex.code}
                    </Option>
                  ))
                  : null}
              </Select>

              <Input
                placeholder="交易对（支持模糊）"
                value={filters.pair}
                onChange={(e) => handleFilterChange("pair", e.target.value)}
                style={{ width: 150 }}
                allowClear
              />

              <Input
                placeholder="数据来源 source"
                value={filters.source}
                onChange={(e) => handleFilterChange("source", e.target.value)}
                style={{ width: 140 }}
                allowClear
              />

              {isAdmin && (
                <>
                  <Input
                    placeholder="Token ID"
                    value={filters.token_id}
                    onChange={(e) =>
                      handleFilterChange("token_id", e.target.value)
                    }
                    style={{ width: 100 }}
                    allowClear
                  />
                  <Input
                    placeholder="记录哈希（精确）"
                    value={filters.hash}
                    onChange={(e) => handleFilterChange("hash", e.target.value)}
                    style={{ width: 200 }}
                    allowClear
                  />
                </>
              )}

              <Select
                placeholder="时间筛选"
                value={timePreset || undefined}
                onChange={(value) => {
                  setTimePreset(value);
                  if (value === "custom") {
                    setShowCustomRange(true);
                    // 不立即改 dateRange，等用户选择
                  } else {
                    setShowCustomRange(false);
                    const range = getDateRangeFromPreset(value);
                    handleFilterChange("dateRange", range);
                  }
                }}
                style={{ width: 130 }}
                allowClear
                onClear={() => {
                  setTimePreset("");
                  setShowCustomRange(false);
                  handleFilterChange("dateRange", null);
                }}
              >
                {timePresets.map((p) => (
                  <Option key={p.value} value={p.value}>
                    {p.label}
                  </Option>
                ))}
              </Select>
              {showCustomRange && (
                <RangePicker
                  value={filters.dateRange}
                  onChange={(value) => handleFilterChange("dateRange", value)}
                  format="YYYY-MM-DD"
                  placeholder={["开始日期", "结束日期"]}
                  style={{ minWidth: 240 }}
                />
              )}

              <Select
                placeholder="交易类型"
                style={{ width: 120 }}
                allowClear
                value={filters.trade_type || undefined}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, trade_type: value || "" }))
                }
              >
                <Option value="spot">现货</Option>
                <Option value="swap">合约</Option>
              </Select>

              <Select
                placeholder="选择账号（含交易所与类型）"
                value={accountSelectValue}
                onChange={handleAccountFilterChange}
                style={{ width: 260 }}
                allowClear
                showSearch
                loading={loadingAccounts}
                filterOption={(input, option) =>
                  (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
                }
              >
                {activeAccounts.map((item) => {
                  const v = `${item.account}${ACCOUNT_SCOPE_SEP}${item.exchange}${ACCOUNT_SCOPE_SEP}${item.trade_type}`;
                  return (
                    <Option
                      key={v}
                      value={v}
                      label={`${item.account} ${item.exchange} ${item.trade_type}`}
                    >
                      {item.account} ({formatExchangeName(item.exchange)}) ·{" "}
                      {formatTradeType(item.trade_type)}
                    </Option>
                  );
                })}
              </Select>

              <Button
                type="primary"
                onClick={handleSearch}
                icon={<SearchOutlined />}
              >
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
              <Button onClick={handleRefresh} icon={<ReloadOutlined />}>
                刷新
              </Button>
              <Button onClick={handleExport} icon={<DownloadOutlined />}>
                导出
              </Button>
              {isAdmin && (
                <Button type="primary" onClick={handleOpenAdd}>
                  新增记录
                </Button>
              )}
              {isAdmin && viewMode === "list" && (
                <Popconfirm
                  title={`确定要删除选中的 ${selectedRowKeys.length} 条交易数据吗？`}
                  description="此操作不可恢复。"
                  onConfirm={handleBatchDelete}
                  okText="确定"
                  cancelText="取消"
                  disabled={selectedRowKeys.length === 0}
                >
                  <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={selectedRowKeys.length === 0}
                  >
                    批量删除
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </div>

          {/* 视图模式切换 */}
          <div className="view-mode-toggle">
            <Radio.Group
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
            >
              <Radio.Button value="list">列表视图</Radio.Button>
              <Radio.Button value="grouped">
                <GroupOutlined /> 按交易所分组
              </Radio.Button>
              <Radio.Button value="byAccount">按账号分组</Radio.Button>
            </Radio.Group>
          </div>

          {/* 数据表格 */}
          <div className="data-table">
            <Table
              columns={
                viewMode === "list"
                  ? listColumns
                  : viewMode === "byAccount"
                    ? groupedColumnsByAccount
                    : groupedColumns
              }
              dataSource={viewMode === "list" ? data : groupedData}
              scroll={{ x: "max-content" }}
              pagination={
                viewMode === "list"
                  ? {
                    ...pagination,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `共 ${total} 条记录`,
                  }
                  : false
              }
              loading={loading}
              onChange={handleTableChange}
              rowSelection={
                viewMode === "list" && isAdmin
                  ? {
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                  }
                  : null
              }
              rowKey={
                viewMode === "list"
                  ? "id"
                  : viewMode === "byAccount"
                    ? "key"
                    : "exchange"
              }
              size="small"
              bordered
              summary={
                viewMode === "grouped"
                  ? () => (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                        <Table.Summary.Cell index={0}>
                          <span style={{ fontWeight: 600 }}>总计</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <span style={{ fontWeight: 600 }}>
                            {stats.recordCount}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontWeight: 600,
                              color: "var(--color-primary)",
                            }}
                          >
                            {formatVolume(stats.totalVolume)}
                          </span>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )
                  : viewMode === "byAccount" && accountGroupSummary
                    ? () => (
                      <Table.Summary fixed>
                        <Table.Summary.Row style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                          <Table.Summary.Cell index={0} colSpan={2}>
                            <span style={{ fontWeight: 600 }}>本页合计（当前筛选结果）</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">
                            <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#52c41a" }}>
                              {formatVolume(accountGroupSummary.spotVolume)}
                            </span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="right">
                            <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#722ed1" }}>
                              {formatVolume(accountGroupSummary.swapVolume)}
                            </span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right">
                            <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#13c2c2" }}>
                              {formatVolume(accountGroupSummary.totalVolume)}
                            </span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="right">
                            <span style={{ fontWeight: 600 }}>{accountGroupSummary.count}</span>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )
                    : null
              }
            />
          </div>
        </Card>

        {/* 新增记录弹窗 */}
        <Modal
          title="新增交易记录"
          open={isAddModalOpen}
          onOk={handleAddSubmit}
          onCancel={() => setIsAddModalOpen(false)}
          destroyOnHidden
        >
          <Form form={addForm} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="exchange" label="交易所" rules={[{ required: true, message: "请选择交易所" }]}>
                  <Select placeholder="选择交易所" showSearch loading={loadingExchanges}>
                    {exchanges.map((ex) => (
                      <Option key={ex.code || ex.name} value={ex.code || ex.name}>
                        {ex.name || ex.code}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="trade_type" label="交易类型" rules={[{ required: true, message: "请选择交易类型" }]}>
                  <Select placeholder="选择类型">
                    <Option value="spot">现货</Option>
                    <Option value="swap">合约</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="account" label="账号标识" rules={[{ required: true, message: "请输入账号" }]}>
                  <Input placeholder="例如: user@example.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="pair" label="交易对" rules={[{ required: true, message: "请输入交易对" }]}>
                  <Input placeholder="例如: BTC/USDT" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="volume" label="交易量" rules={[{ required: true, message: "请输入交易量" }]}>
                  <Input type="number" step="0.00000001" placeholder="设定交易量" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="price" label="交易对参考价">
                  <Input type="number" step="0.00000001" placeholder="标的/交易对市价" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="fee_currency" label="手续费币种">
                  <Input placeholder="如 USDT、HTX" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="fee_token_price"
                  label="手续费币价(USDT)"
                  tooltip="1 单位手续费币折合多少 USDT；手续费为 USDT 时填 1"
                >
                  <Input type="number" step="0.00000001" placeholder="默认由后端推断" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="timestamp" label="交易时间" rules={[{ required: true, message: "请选择交易时间" }]}>
              <DatePicker showTime style={{ width: "100%" }} />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="created_at" label="创建时间">
                  <DatePicker showTime style={{ width: "100%" }} placeholder="选填, 留空默认为当前" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="updated_at" label="更新时间">
                  <DatePicker showTime style={{ width: "100%" }} placeholder="选填, 留空默认为当前" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* 编辑记录弹窗 */}
        <Modal
          title="编辑交易记录"
          open={isEditModalOpen}
          onOk={() => form.submit()}
          onCancel={() => setIsEditModalOpen(false)}
          destroyOnHidden
        >
          <Form form={form} layout="vertical" onFinish={handleEditSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="exchange" label="交易所" rules={[{ required: true, message: "请选择交易所" }]}>
                  <Select placeholder="选择交易所" showSearch loading={loadingExchanges}>
                    {exchanges.map((ex) => (
                      <Option key={ex.code || ex.name} value={ex.code || ex.name}>
                        {ex.name || ex.code}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="trade_type" label="交易类型" rules={[{ required: true, message: "请选择交易类型" }]}>
                  <Select placeholder="选择类型">
                    <Option value="spot">现货</Option>
                    <Option value="swap">合约</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="account" label="账号标识" rules={[{ required: true, message: "请输入账号" }]}>
                  <Input placeholder="例如: user@example.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="pair" label="交易对" rules={[{ required: true, message: "请输入交易对" }]}>
                  <Input placeholder="例如: BTC/USDT" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="volume" label="交易量" rules={[{ required: true, message: "请输入交易量" }]}>
                  <Input type="number" step="0.00000001" placeholder="设定交易量" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="price" label="交易对参考价">
                  <Input type="number" step="0.00000001" placeholder="标的/交易对市价" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="fee_currency" label="手续费币种">
                  <Input placeholder="如 USDT、HTX" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="fee_token_price"
                  label="手续费币价(USDT)"
                  tooltip="1 单位手续费币折合多少 USDT"
                >
                  <Input type="number" step="0.00000001" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="timestamp" label="交易时间" rules={[{ required: true, message: "请选择交易时间" }]}>
              <DatePicker showTime style={{ width: "100%" }} />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="created_at" label="创建时间">
                  <DatePicker showTime style={{ width: "100%" }} placeholder="覆盖原创建时间" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="updated_at" label="更新时间">
                  <DatePicker showTime style={{ width: "100%" }} placeholder="覆盖原更新时间" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

export default TradeDataTable;
